/**
 * Lista de Remarketing de perdidos (#6419). Regras puras da tela; o banco põe e tira
 * negociações da lista (migration 20261030125000_remarketing_perdidos.sql).
 *
 * Quem trabalha: a BDR. Quem vê: BDR, gerente e adm (a RLS repete a regra).
 * Sem disparo automático: a BDR manda mensagem 1 a 1 e exporta o público para o Meta.
 */

export type EtapaRemarketing = 'na_fila' | 'em_nutricao' | 'recuperado' | 'descartado';
export type SituacaoRemarketing = 'pronto' | 'aguardando' | 'em_nutricao' | 'recuperado' | 'descartado';
export type ViaRecuperacao = 'remarketing' | 'reativada' | 'novo_lead';

/** Uma linha da view remarketing_lista. */
export interface LinhaRemarketing {
  negociacao_id: number;
  motivo_perda: string;
  perdido_em: string;
  reabordar_em: string;
  etapa: EtapaRemarketing;
  tentativas: number;
  ultima_tentativa_em: string | null;
  nao_contatar: boolean;
  descarte_motivo: string | null;
  recuperado_em: string | null;
  recuperado_via: ViaRecuperacao | null;
  recuperado_negociacao_id: number | null;
  updated_at: string;
  /** Normalizado: 55 + DDD + número. null = telefone inválido. */
  telefone: string | null;
  cliente_nome: string | null;
  whatsapp: string | null;
  origem: string | null;
  campanha: string | null;
  etapa_funil: string | null;
  cidade: string | null;
  consumo_medio_kwh: number | null;
  valor_conta_luz: string | null;
  observacoes_perda: string | null;
  responsavel_id: string | null;
  /** Já existe outra negociação ativa (ou cliente) com o mesmo telefone ou CPF/CNPJ. */
  tem_outra_ativa: boolean;
  /** Para a mesma pessoa perdida mais de uma vez, só a perda mais recente é a principal. */
  principal: boolean;
}

interface PerfilRemarketing {
  role?: string | null;
  funcao_comercial?: string | null;
}

/** BDR trabalha a lista; gerente e adm acompanham (decisão do Felipe, 30/10/2026). */
export const podeTrabalharRemarketing = (p: PerfilRemarketing | null | undefined): boolean =>
  !!p && (p.role === 'adm' || p.role === 'gerente' || p.funcao_comercial === 'bdr');

export const ROTULO_SITUACAO: Record<SituacaoRemarketing, string> = {
  pronto: 'Prontos para reabordar',
  aguardando: 'Aguardando',
  em_nutricao: 'Em nutrição',
  recuperado: 'Recuperados',
  descartado: 'Descartados',
};

export const ROTULO_VIA: Record<ViaRecuperacao, string> = {
  remarketing: 'Voltou ao funil pelo remarketing',
  reativada: 'Reativada no funil depois da reabordagem',
  novo_lead: 'Voltou sozinha como lead novo (anúncio, site…)',
};

export const MOTIVOS_DESCARTE = [
  'Pediu para não receber contato',
  'Telefone inválido ou de outra pessoa',
  'Sem interesse definitivo',
  'Já é cliente ou tem outra negociação',
  'Outro',
] as const;

/** Quem pede para não receber contato não volta para a lista nem se for perdido de novo. */
export const descarteBloqueiaContato = (motivo: string) => motivo === MOTIVOS_DESCARTE[0];

export const OPCOES_ADIAR_DIAS = [7, 15, 30, 60] as const;

/** Situação na tela. "Pronto" e "aguardando" saem da data, não de uma etapa gravada. */
export function situacaoRemarketing(l: Pick<LinhaRemarketing, 'etapa' | 'reabordar_em'>, agora: Date = new Date()): SituacaoRemarketing {
  if (l.etapa === 'na_fila') return new Date(l.reabordar_em).getTime() <= agora.getTime() ? 'pronto' : 'aguardando';
  return l.etapa;
}

/**
 * Entra na contagem e na lista? Para quem ainda está sendo trabalhado, some a perda
 * repetida da mesma pessoa e quem já tem outra negociação ativa (não faz sentido
 * reabordar quem já está no funil ou é cliente). Recuperados e descartados aparecem todos.
 */
export function visivelNaLista(l: Pick<LinhaRemarketing, 'etapa' | 'principal' | 'tem_outra_ativa'>): boolean {
  if (l.etapa === 'recuperado' || l.etapa === 'descartado') return true;
  return l.principal && !l.tem_outra_ativa;
}

export function contarPorSituacao(linhas: LinhaRemarketing[], agora: Date = new Date()): Record<SituacaoRemarketing, number> {
  const c: Record<SituacaoRemarketing, number> = { pronto: 0, aguardando: 0, em_nutricao: 0, recuperado: 0, descartado: 0 };
  for (const l of linhas) if (visivelNaLista(l)) c[situacaoRemarketing(l, agora)]++;
  return c;
}

export function primeiroNome(nome: string | null | undefined): string {
  const p = (nome ?? '').trim().split(/\s+/)[0] ?? '';
  if (!p) return '';
  return p.charAt(0).toLocaleUpperCase('pt-BR') + p.slice(1).toLocaleLowerCase('pt-BR');
}

const ROTEIRO_PADRAO =
  'Oi, {nome}! Aqui é {atendente}, da Vigor Energy. Conversamos há um tempo sobre economizar na conta de luz. Ainda tem interesse? É só responder esta mensagem.';

/** Mensagem pronta para a reabordagem. Sem nome do cliente, a saudação fica sem nome. */
export function montarMensagem(roteiro: string | null | undefined, cliente: string | null | undefined, atendente: string | null | undefined): string {
  const nome = primeiroNome(cliente);
  return (roteiro || ROTEIRO_PADRAO)
    .replace(/\{nome\}/g, nome)
    .replace(/\{atendente\}/g, primeiroNome(atendente) || 'a equipe')
    .replace(/^Oi, !/, 'Oi!');
}

/** Link do WhatsApp com a mensagem já escrita. null quando o telefone é inválido. */
export function linkWhatsApp(telefone: string | null | undefined, texto: string): string | null {
  if (!telefone || !/^55\d{10,11}$/.test(telefone)) return null;
  return `https://wa.me/${telefone}?text=${encodeURIComponent(texto)}`;
}

/** (62) 99111-0001 a partir de 5562991110001. */
export function formatarTelefone(telefone: string | null | undefined): string {
  if (!telefone || !/^55\d{10,11}$/.test(telefone)) return '';
  const ddd = telefone.slice(2, 4);
  const num = telefone.slice(4);
  return num.length === 9 ? `(${ddd}) ${num.slice(0, 5)}-${num.slice(5)}` : `(${ddd}) ${num.slice(0, 4)}-${num.slice(4)}`;
}

export function diasDesde(iso: string, agora: Date = new Date()): number {
  return Math.max(0, Math.floor((agora.getTime() - new Date(iso).getTime()) / 86_400_000));
}

/**
 * Público personalizado do Meta (lista de clientes): telefone com DDI, primeiro nome
 * e país, um por pessoa. Telefone inválido fica de fora — o Meta não acharia ninguém.
 */
export function csvPublicoMeta(linhas: Pick<LinhaRemarketing, 'telefone' | 'cliente_nome'>[]): { csv: string; total: number } {
  const vistos = new Set<string>();
  const corpo: string[] = [];
  for (const l of linhas) {
    if (!l.telefone || !/^55\d{10,11}$/.test(l.telefone) || vistos.has(l.telefone)) continue;
    vistos.add(l.telefone);
    const fn = primeiroNome(l.cliente_nome).toLocaleLowerCase('pt-BR').replace(/[",;\n\r]/g, '');
    corpo.push(`${l.telefone},${fn},BR`);
  }
  return { csv: ['phone,fn,country', ...corpo].join('\n'), total: corpo.length };
}
